import * as ReactHelmetAsync from 'react-helmet-async';
const Helmet = ReactHelmetAsync.Helmet || (ReactHelmetAsync as any).default?.Helmet;
import { useState } from 'react';
import type { FormEvent } from 'react';
import { MapPin, Send } from 'lucide-react';
import { Navigation } from '@/components/layout/Navigation';
import { Footer } from '@/components/layout/Footer';
import { PageHero } from '@/components/sections/PageHero';

const flyingGoals = [
  'Tailwheel Endorsement',
  'CFI Spin Training',
  'Tailwheel Refresh',
  'Flight Review',
  'Other',
];

export function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    flyingGoal: '',
    otherGoal: '',
    message: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    const payload: Record<string, string> = {
      name: formData.name,
      email: formData.email,
    };
    if (formData.phone) payload.phone = formData.phone;
    if (formData.message) payload.message = formData.message;
    if (formData.flyingGoal) {
      payload.flyingGoal =
        formData.flyingGoal === 'Other'
          ? formData.otherGoal || 'Other'
          : formData.flyingGoal;
    }

    try {
      const response = await fetch('https://formspree.io/f/xeerlqdk', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        setSubmitted(true);
      } else {
        setError('Something went wrong. Please try again or email us directly.');
      }
    } catch {
      setError('Something went wrong. Please try again or email us directly.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Inquire Now | Total Tailwheel</title>
        <meta
          name="description"
          content="Contact Total Tailwheel for tailwheel training at Minute Man Air Field near Boston. Get started with your tailwheel endorsement, flight review, or CFI spin training."
        />
        <meta property="og:title" content="Inquire Now | Total Tailwheel" />
        <meta
          property="og:description"
          content="Get in touch with Total Tailwheel for flight training inquiries."
        />
        <link rel="canonical" href="https://totaltailwheel.com/inquire" />
      </Helmet>

      <div className="min-h-screen bg-gray-50">
        <Navigation />

        <PageHero title="Inquire Now" />

        <section className="py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-2xl mx-auto">
              <div className="bg-white p-8 rounded-lg shadow-md">
                <h2 className="text-2xl font-medium text-primary mb-6">Get in Touch</h2>

                {submitted ? (
                  <div className="text-center py-8">
                    <div className="bg-green-50 text-green-800 rounded-lg p-6">
                      <p className="text-lg font-medium mb-2">Thanks for reaching out!</p>
                      <p className="text-sm">
                        We'll get back to you shortly.
                      </p>
                    </div>
                    <button
                      onClick={() => {
                        setSubmitted(false);
                        setFormData({ name: '', email: '', phone: '', flyingGoal: '', otherGoal: '', message: '' });
                      }}
                      className="mt-4 text-primary hover:text-primary/80 text-sm underline"
                    >
                      Send another message
                    </button>
                  </div>
                ) : (
                  <form action="https://formspree.io/f/xeerlqdk" method="POST" onSubmit={handleSubmit} className="space-y-5">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-foreground/80 mb-1">
                        Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        required
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none transition-colors"
                      />
                    </div>

                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-foreground/80 mb-1">
                        Email <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        required
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none transition-colors"
                      />
                    </div>

                    <div>
                      <label htmlFor="phone" className="block text-sm font-medium text-foreground/80 mb-1">
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none transition-colors"
                      />
                    </div>

                    <div>
                      <label htmlFor="flyingGoal" className="block text-sm font-medium text-foreground/80 mb-1">
                        Flying Goal
                      </label>
                      <select
                        id="flyingGoal"
                        name="flyingGoal"
                        value={formData.flyingGoal}
                        onChange={(e) => setFormData({ ...formData, flyingGoal: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none transition-colors bg-white"
                      >
                        <option value="">Select a goal (optional)</option>
                        {flyingGoals.map((goal) => (
                          <option key={goal} value={goal}>
                            {goal}
                          </option>
                        ))}
                      </select>
                    </div>

                    {formData.flyingGoal === 'Other' && (
                      <div>
                        <label htmlFor="otherGoal" className="block text-sm font-medium text-foreground/80 mb-1">
                          Please specify
                        </label>
                        <input
                          type="text"
                          id="otherGoal"
                          name="otherGoal"
                          value={formData.otherGoal}
                          onChange={(e) => setFormData({ ...formData, otherGoal: e.target.value })}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none transition-colors"
                          placeholder="Describe your flying goal"
                        />
                      </div>
                    )}

                    <div>
                      <label htmlFor="message" className="block text-sm font-medium text-foreground/80 mb-1">
                        Message
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        rows={4}
                        value={formData.message}
                        onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none transition-colors resize-y"
                        placeholder="Anything else you'd like us to know?"
                      />
                    </div>

                    {error && (
                      <div className="text-red-600 text-sm bg-red-50 rounded-lg p-3">
                        {error}
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={submitting}
                      className="w-full bg-primary text-white px-6 py-3 rounded-lg hover:bg-primary/90 transition-colors font-medium flex items-center justify-center gap-2 disabled:opacity-60"
                    >
                      <Send size={18} />
                      {submitting ? 'Sending...' : 'Send Message'}
                    </button>
                  </form>
                )}
              </div>

              <p className="text-center text-sm text-foreground/50 mt-6">
                Or email us directly at{' '}
                <a
                  href="mailto:kevin@totaltailwheel.com"
                  className="text-primary hover:text-primary/80 transition-colors"
                >
                  kevin@totaltailwheel.com
                </a>
              </p>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}
